import { Fragment } from 'react'
import data from './data/timelineData.json'
import './Login.css'

const VIEWBOX_WIDTH = 600
const VIEWBOX_HEIGHT = 375
const ALEPH_X = 300
const ALEPH_Y = 225
const ALEPH_FONT_SIZE = 240
const ALEPH_FONT_FAMILY = "Georgia, 'Times New Roman', serif"

type Point = { x: number; y: number }

type Dataset = {
  shortcode: string
  color: string
  pulseClass: string
  labelAnchor: Point
  points: Point[]
}

type Annotation = { label: string; x: number; y: number }

type TimelineData = { datasets: Dataset[]; annotations: Annotation[] }

const typedData = data as TimelineData

const toPointsString = (points: Point[]): string => points.map((p) => `${p.x},${p.y}`).join(' ')

type ChartCopyProps = { datasets: Dataset[]; vivid: boolean }

const ChartCopy = ({ datasets, vivid }: ChartCopyProps) => (
  <>
    {datasets.map((dataset) => (
      // pulseClass applied to both layers: full opacity oscillation inside the lens,
      // and a subtle 0.12–0.22 micro-pulse in the faint background layer.
      <polyline
        key={`line-${dataset.shortcode}`}
        points={toPointsString(dataset.points)}
        fill="none"
        stroke={dataset.color}
        strokeWidth={vivid ? 2.5 : 0.6}
        filter={vivid ? 'url(#lineGlow)' : undefined}
        className={dataset.pulseClass}
      />
    ))}
    {vivid &&
      datasets.map((dataset) => (
        <g
          key={`nodes-${dataset.shortcode}`}
          fill={dataset.color}
          filter="url(#lineGlow)"
          className={dataset.pulseClass}
        >
          {dataset.points.map((p) => (
            <circle key={`${p.x}-${p.y}`} cx={p.x} cy={p.y} r={3} />
          ))}
        </g>
      ))}
    {vivid &&
      datasets.map((dataset) => (
        <text
          key={`label-${dataset.shortcode}`}
          x={dataset.labelAnchor.x}
          y={dataset.labelAnchor.y}
          textAnchor="start"
          fontFamily="ui-monospace, monospace"
          fontSize={11}
          fontWeight={700}
          fill={dataset.color}
          filter="url(#lineGlow)"
          letterSpacing={1}
          className={dataset.pulseClass}
        >
          [{dataset.shortcode}]
        </text>
      ))}
  </>
)

type ChartLayerProps = { datasets: Dataset[]; vivid: boolean }

const ChartLayer = ({ datasets, vivid }: ChartLayerProps) => (
  <g className="lens-pan" style={vivid ? undefined : { opacity: 0.22 }}>
    <g>
      <ChartCopy datasets={datasets} vivid={vivid} />
    </g>
    <g transform={`translate(${VIEWBOX_WIDTH},0)`}>
      <ChartCopy datasets={datasets} vivid={vivid} />
    </g>
  </g>
)

type AnnotationCopyProps = { annotations: Annotation[] }

const AnnotationCopy = ({ annotations }: AnnotationCopyProps) => (
  <>
    {annotations.map((ann) => (
      <Fragment key={ann.label}>
        <line
          x1={ann.x}
          y1={0}
          x2={ann.x}
          y2={VIEWBOX_HEIGHT}
          stroke="rgba(255,255,255,0.15)"
          strokeDasharray="4 4"
          strokeWidth={1}
        />
        <text
          x={ann.x + 6}
          y={ann.y}
          fontFamily="ui-monospace, monospace"
          fontSize={5}
          fill="rgba(255,255,255,0.6)"
          letterSpacing={0.5}
        >
          {ann.label}
        </text>
      </Fragment>
    ))}
  </>
)

type AnnotationLayerProps = { annotations: Annotation[] }

const AnnotationLayer = ({ annotations }: AnnotationLayerProps) => (
  <g className="lens-pan">
    <g>
      <AnnotationCopy annotations={annotations} />
    </g>
    <g transform={`translate(${VIEWBOX_WIDTH},0)`}>
      <AnnotationCopy annotations={annotations} />
    </g>
  </g>
)

export function NeonBackground() {
  const { datasets, annotations } = typedData

  return (
    <div
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: '#0a0a0f',
        overflow: 'hidden',
        pointerEvents: 'none',
        zIndex: 0,
      }}
    >
      <svg
        viewBox={`0 0 ${VIEWBOX_WIDTH} ${VIEWBOX_HEIGHT}`}
        preserveAspectRatio="xMidYMid slice"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: '100%',
          height: '100%',
        }}
      >
        <defs>
          <mask
            id="alephReveal"
            maskUnits="userSpaceOnUse"
            maskContentUnits="userSpaceOnUse"
            x={0}
            y={0}
            width={VIEWBOX_WIDTH}
            height={VIEWBOX_HEIGHT}
          >
            <rect width={VIEWBOX_WIDTH} height={VIEWBOX_HEIGHT} fill="#000" />
            <text
              x={ALEPH_X}
              y={ALEPH_Y}
              textAnchor="middle"
              fontFamily={ALEPH_FONT_FAMILY}
              fontSize={ALEPH_FONT_SIZE}
              fontWeight={500}
              fill="#fff"
            >
              א
            </text>
          </mask>

          <filter id="lineGlow" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation={1.4} result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>

          <radialGradient id="vignetteGrad" cx="50%" cy="50%" r="70%">
            <stop offset="40%" stopColor="rgba(10,10,15,0)" />
            <stop offset="100%" stopColor="rgba(10,10,15,0.85)" />
          </radialGradient>
        </defs>

        {/* LAYER 1 — Faint chart, full canvas, panning. */}
        <ChartLayer datasets={datasets} vivid={false} />

        {/* LAYER 2 — Vivid chart, masked by static aleph; panning happens INSIDE the static mask. */}
        <g mask="url(#alephReveal)">
          <ChartLayer datasets={datasets} vivid={true} />
        </g>

        {/* LAYER 3 — Always-visible timeline annotations, panning, NOT masked. */}
        <AnnotationLayer annotations={annotations} />

        {/* LAYER 4 — Static stroke-only aleph outline (anchors the brand mark). */}
        <text
          x={ALEPH_X}
          y={ALEPH_Y}
          textAnchor="middle"
          fontFamily={ALEPH_FONT_FAMILY}
          fontSize={ALEPH_FONT_SIZE}
          fontWeight={500}
          fill="none"
          stroke="rgba(255,255,255,0.16)"
          strokeWidth={0.8}
        >
          א
        </text>

        {/* LAYER 5 — Vignette. */}
        <rect
          width={VIEWBOX_WIDTH}
          height={VIEWBOX_HEIGHT}
          fill="url(#vignetteGrad)"
          pointerEvents="none"
        />
      </svg>
    </div>
  )
}
